/** Admin Quote Details — parity with the Booking details modal:
 *  info grid, price breakdown, WhatsApp card, Stripe payment-link controls
 *  (copy / open / resend deposit / switch to full) and status pipeline. */
import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { SendBookingWhatsAppButton } from "@/components/SendBookingWhatsAppButton";
import api from "@/lib/api";
import { Copy, Link2, Send, Receipt } from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "confirmed", "in_progress", "completed", "cancelled"];
const STATUS_COLOR = {
  new: "border-blue-400/50 text-blue-300",
  contacted: "border-sky-400/50 text-sky-300",
  confirmed: "border-emerald-400/50 text-emerald-300",
  in_progress: "border-amber-400/50 text-amber-300",
  completed: "border-[#C5A059]/60 text-[#C5A059]",
  cancelled: "border-red-400/50 text-red-300",
};

const Info = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
    <p className="text-sm text-zinc-200 mt-0.5 break-words">{value || "—"}</p>
  </div>
);

export default function QuoteDetailsDialog({ quote, onClose, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const q = quote;
  if (!q) return null;
  const mode = q.payment_mode || "deposit";
  const activeUrl = mode === "full" ? q.full_payment_url : q.deposit_payment_url;
  const activeAmt = mode === "full" ? q.full_payment_amount : q.deposit_payment_amount;

  const whatsAppMessage = [
    `Hello ${q.guest_name?.split(" ")[0] || ""}! 👋`,
    ``,
    `Your Juke Transfers quote ${q.ref_number}:`,
    `📍 ${q.pickup} → ${q.dropoff || "at disposal"}`,
    q.pickup_date ? `📅 ${q.pickup_date}${q.pickup_time ? ` · ${q.pickup_time}` : ""}` : null,
    `🚘 ${q.vehicle_type} · ${q.passengers} pax`,
    `💶 Total: €${Number(q.total_price_eur || 0).toFixed(2)}`,
    q.deposit_payment_url ? `` : null,
    q.deposit_payment_url ? `Secure your booking with the deposit here:` : null,
    q.deposit_payment_url || null,
    ``,
    `Juke Transfers · Split`,
  ].filter((l) => l !== null).join("\n");

  const resend = async (m) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/quotes/${q.id}/resend-payment-link`, { mode: m });
      toast.success(
        `${m === "full" ? "100% full" : "Deposit"} link ${data.emailed ? "sent to guest" : "regenerated"} — €${Number(data.amount).toFixed(2)}`,
      );
      onUpdated({
        ...q,
        payment_mode: m,
        [`${m}_payment_url`]: data.url,
        [`${m}_payment_amount`]: data.amount,
        payment_link_resent_at: new Date().toISOString(),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe link failed.");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (s) => {
    try {
      await api.patch(`/admin/quotes/${q.id}/pipeline-status`, { status: s });
      onUpdated({ ...q, pipeline_status: s });
    } catch {
      toast.error("Status update failed.");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="quote-details-dialog"
        className="bg-[#0d0d0d] border-white/10 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white font-light flex items-center gap-3">
            {q.guest_name}
            {q.paid && (
              <span
                data-testid="quote-paid-badge"
                className="text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/60 text-emerald-300 font-semibold"
              >
                ✓ Paid{q.paid_amount_eur ? ` · €${Number(q.paid_amount_eur).toFixed(2)}` : ""}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {q.ref_number} · Created {q.created_at ? new Date(q.created_at).toLocaleString() : "—"}
            {q.paid && q.paid_at ? ` · Paid ${new Date(q.paid_at).toLocaleString()} (${q.paid_mode === "full" ? "100% full" : "deposit"})` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-6 gap-y-4 mt-2">
          <Info label="Email" value={q.guest_email} />
          <Info label="Phone" value={q.guest_phone} />
          <Info label="Pickup" value={q.pickup} />
          <Info label="Drop-off" value={q.dropoff} />
          <Info label="Date" value={q.pickup_date} />
          <Info label="Time" value={q.pickup_time} />
          <Info label="Passengers" value={q.passengers} />
          <Info label="Luggage" value={q.luggage} />
          <Info label="Vehicle" value={q.vehicle_type} />
          <Info label="Route" value={`${q.distance_km ?? "—"} km · ${q.duration_min ?? "—"} min`} />
        </div>

        {(q.line_items || []).length > 0 && (
          <div className="mt-5 rounded-xl border border-white/10 p-4" data-testid="quote-breakdown">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-2 flex items-center gap-1.5">
              <Receipt className="w-3.5 h-3.5" /> Price breakdown
            </p>
            {q.line_items.map((li, i) => (
              <div key={i} className="flex justify-between text-xs py-1 border-b border-white/5 last:border-0">
                <span className="text-zinc-400 truncate pr-3">{li.qty > 1 ? `${li.qty}× ` : ""}{li.label}</span>
                <span className="text-zinc-200 tabular-nums shrink-0">€{Number(li.total_eur).toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm pt-2 font-semibold">
              <span className="text-[#C5A059]">TOTAL PRICE</span>
              <span className="text-[#C5A059] tabular-nums">€{Number(q.total_price_eur || 0).toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* WhatsApp card */}
        <div
          data-testid={`quote-whatsapp-card-${q.id}`}
          className="mt-5 rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-4 flex items-center gap-3 flex-wrap"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white font-medium">Quote confirmation via WhatsApp</p>
            <p className="text-xs text-zinc-400">
              Opens a pre-composed message (route, date, time, total, Stripe deposit link) — you tap Send in WhatsApp.
            </p>
          </div>
          <SendBookingWhatsAppButton
            booking={{ id: q.id, phone: q.guest_phone, name: q.guest_name }}
            defaultMessage={whatsAppMessage}
          />
        </div>

        {/* Stripe control section */}
        {(q.deposit_payment_url || q.full_payment_url) && (
          <div
            data-testid={`quote-payment-card-${q.id}`}
            className="mt-5 rounded-xl bg-blue-500/5 border border-blue-500/20 p-4"
          >
            <div className="flex items-start gap-3">
              <Link2 className="w-5 h-5 text-blue-300 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium">
                  {mode === "full" ? "Full-payment link" : "Deposit payment link"}
                  <span className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 text-zinc-400">
                    {mode === "full" ? "100% Full" : "Deposit"}
                    {activeAmt ? ` · €${Number(activeAmt).toFixed(2)}` : ""}
                  </span>
                </p>
                {q.payment_links_created_at && (
                  <p className="text-[11px] text-zinc-400 mt-0.5">
                    Created {new Date(q.payment_links_created_at).toLocaleString()}
                  </p>
                )}
                {q.payment_link_resent_at && (
                  <p className="mt-1 text-[11px] text-blue-300">
                    🔁 Resent {new Date(q.payment_link_resent_at).toLocaleString()}
                  </p>
                )}
                {q.reminder_sent_at && (
                  <p className="mt-1 text-[11px] text-amber-300">
                    ⏰ Reminder sent {new Date(q.reminder_sent_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                data-testid={`quote-copy-link-${q.id}`}
                onClick={() => { navigator.clipboard.writeText(activeUrl || ""); toast.success("Payment link copied"); }}
                disabled={!activeUrl}
                className="h-9 px-4 rounded-full border border-white/15 hover:border-blue-400 text-blue-200 text-[11px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors disabled:opacity-40"
              >
                <Copy className="w-3.5 h-3.5" /> Copy link
              </button>
              <a
                href={activeUrl || "#"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`quote-open-stripe-${q.id}`}
                className="h-9 px-4 rounded-full border border-white/15 hover:border-blue-400 text-blue-200 text-[11px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors"
              >
                <Link2 className="w-3.5 h-3.5" /> Open Stripe
              </a>
              <button
                type="button"
                data-testid={`quote-resend-link-${q.id}`}
                onClick={() => resend(mode)}
                disabled={busy}
                className="h-9 px-4 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[11px] uppercase tracking-[0.15em] font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" /> Resend {mode === "full" ? "100% Full" : "Deposit"}
              </button>
              <button
                type="button"
                data-testid={`quote-switch-mode-${q.id}`}
                onClick={() => resend(mode === "full" ? "deposit" : "full")}
                disabled={busy}
                className="h-9 px-4 rounded-full border border-white/15 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-[11px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors disabled:opacity-50"
              >
                Switch to {mode === "full" ? "Deposit" : "100% Full"}
              </button>
            </div>
          </div>
        )}

        {/* Status pipeline */}
        <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap gap-2" data-testid="quote-status-pipeline">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              data-testid={`quote-status-${s}`}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.15em] border transition ${
                (q.pipeline_status || "new") === s
                  ? STATUS_COLOR[s]
                  : "border-white/10 text-zinc-500 hover:text-zinc-100"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
